import React, { useState } from 'react';

const PassengerModal = ({ isOpen, onClose, passengerNumber = 1 }) => {
  const [showRedressTooltip, setShowRedressTooltip] = useState(false);
  const [showKnownTravelerTooltip, setShowKnownTravelerTooltip] = useState(false);
  const [showTsaTooltip, setShowTsaTooltip] = useState(false);
  const [showSecondaryRedress, setShowSecondaryRedress] = useState(false);
  const [showSecondaryRedressTooltip, setShowSecondaryRedressTooltip] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: {
      month: '',
      day: '',
      year: ''
    },
    gender: '',
    residency: {
      country: '',
      state: ''
    },
    loyalty: {
      program: '',
      number: ''
    },
    knownTravelerNumber: '',
    knownTravelerCountry: '',
    redressNumber: '',
    redressCountry: '',
    secondaryRedressNumber: '',
    secondaryRedressCountry: ''
  });

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 h-[95vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="h-full">
          <div className="passenger-dialog">
            {/* Desktop Header */}
            <div className="hidden md:block">
              <div className="passenger-profile p-10 border-b" style={{backgroundColor: '#ecf4fe'}}>
                <div className="flex justify-between items-start mb-2">
                  <div className="account-logo-left">
                    <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="32" height="32" viewBox="0 0 32 32" className="w-8 h-8 text-aa-dark-blue" fill="currentColor" aria-hidden="true">
                      <g>
                        <path d="M16,2C8.3,2,2,8.3,2,16s6.3,14,14,14s14-6.3,14-14S23.7,2,16,2z M23,25.7c-2,1.4-4.4,2.3-7,2.3s-5-0.8-7-2.3V23
                            c0-1.7,1.3-3,3-3l8,0c1.7,0,3,1.3,3,3V25.7z M25,23.9V23c0-2.8-2.2-5-5-5l-8,0c-2.8,0-5,2.2-5,5v0.9C5.1,21.8,4,19,4,16
                            C4,9.4,9.4,4,16,4s12,5.4,12,12C28,19,26.9,21.8,25,23.9z M16,6c-2.8,0-5,2.2-5,5s2.2,5,5,5s5-2.2,5-5S18.8,6,16,6z M16,14
                            c-1.7,0-3-1.3-3-3s1.3-3,3-3s3,1.3,3,3S17.7,14,16,14z"/>
                        <rect fill="none" width="32" height="32"/>
                      </g>
                    </svg>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={onClose}
                      className="p-2 border-2 border-aa-blue hover:bg-gray-100 rounded text-aa-blue"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="passenger-name-profile text-3xl font-semibold text-gray-800 pb-4">
                  Passenger {passengerNumber}
                </div>
              </div>
            </div>

            {/* Mobile Header */}
            <div className="md:hidden p-4 border-b flex items-center justify-between">
              <div className="text-xl font-semibold text-aa-blue">
                Passenger {passengerNumber}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Form Content */}
            <div>
              <div className="p-12 pb-0">
                <h3 className="text-xl font-semibold text-gray-800 mb-8">
                  Passenger details
                </h3>

                {/* TSA Notice */}
                <div className="mb-8">
                  <div className="text-[16px] text-gray-600 mb-3 flex items-start">
                    Enter name as printed on government-issued photo ID. TSA Secure Flight rules
                    <div className="relative">
                      <button 
                        type="button" 
                        className="ml-2 text-blue-600 hover:text-blue-800"
                        onMouseEnter={() => setShowTsaTooltip(true)}
                        onMouseLeave={() => setShowTsaTooltip(false)}
                        aria-label="TSA Secure Flight rules"
                      >
                        <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <g clipPath="url(#clip0_7669_34263_tsa)">
                            <path d="M8 1C4.15 1 1 4.15 1 8C1 11.85 4.15 15 8 15C11.85 15 15 11.85 15 8C15 4.15 11.85 1 8 1ZM8 14C4.7 14 2 11.3 2 8C2 4.7 4.7 2 8 2C11.3 2 14 4.7 14 8C14 11.3 11.3 14 8 14ZM8.5 6.5V12H7.5V6.5H8.5ZM8.75 4.75C8.75 5.15 8.4 5.5 8 5.5C7.6 5.5 7.25 5.15 7.25 4.75C7.25 4.35 7.6 4 8 4C8.4 4 8.75 4.35 8.75 4.75Z" fill="currentColor"/>
                          </g>
                          <defs>
                            <clipPath id="clip0_7669_34263_tsa">
                              <rect width="16" height="16" fill="white"/>
                            </clipPath>
                          </defs>
                        </svg>
                        
                        {/* TSA Secure Flight Rules Tooltip */}
                        {showTsaTooltip && (
                          <div 
                            className="absolute top-full left-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                            style={{ transform: 'translateX(-50%)' }}
                          >
                            <div className="p-4">
                              <div className="font-semibold text-black mb-2 text-left">
                                TSA Secure Flight rules
                              </div>
                              <div className="text-sm text-gray-700 text-left">
                                The Transportation Security Administration (TSA) requires you to provide your full name, date of birth, and gender for the purpose of watch list screening, under the authority of 49 U.S.C. section 114, the Intelligence Reform and Terrorism Prevention Act of 2004 and 49 C.F.R parts 1540 and 1560. You may also provide your Redress Number, if available. Failure to provide your full name, date of birth, and gender may result in denial of transport or denial of authority to enter the boarding area. TSA may share information you provide with law enforcement or intelligence agencies or others under its published system of records notice. For more on TSA privacy policies, or to review the system of records notice and the privacy impact assessment, please see the TSA Web site at{' '}
                                <a 
                                  href="https://www.tsa.gov" 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-blue-600 hover:underline"
                                >
                                  www.tsa.gov
                                </a>
                                .
                              </div>
                            </div>
                            {/* Arrow */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-200"></div>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="text-base text-gray-600">
                    ( <span className="text-red-500">•</span> Required)
                  </div>
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      First name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue placeholder-gray-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Middle name
                    </label>
                    <input
                      type="text"
                      value={formData.middleName}
                      onChange={(e) => handleInputChange('middleName', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue placeholder-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Last name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue placeholder-gray-500"
                      required
                    />
                  </div>
                </div>

                {/* Date of Birth and Gender */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Date of birth <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="relative">
                        <select
                          value={formData.dateOfBirth.month}
                          onChange={(e) => handleInputChange('dateOfBirth.month', e.target.value)}
                          className="px-3 py-3 pr-8 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue bg-white appearance-none w-full"
                          required
                        >
                          <option value="">Month</option>
                          <option value="01">January</option>
                          <option value="02">February</option>
                          <option value="03">March</option>
                          <option value="04">April</option>
                          <option value="05">May</option>
                          <option value="06">June</option>
                          <option value="07">July</option>
                          <option value="08">August</option>
                          <option value="09">September</option>
                          <option value="10">October</option>
                          <option value="11">November</option>
                          <option value="12">December</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none text-aa-dark-blue">
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <g clipPath="url(#clip0_6925_33198_month)">
                              <path d="M8 11.5L3.5 6H12.5L8 11.5Z" fill="currentColor"></path>
                            </g>
                            <defs>
                              <clipPath id="clip0_6925_33198_month">
                                <rect width="16" height="16" fill="white" transform="translate(16) rotate(90)"></rect>
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </div>
                      <div className="relative">
                        <select
                          value={formData.dateOfBirth.day}
                          onChange={(e) => handleInputChange('dateOfBirth.day', e.target.value)}
                          className="px-3 py-3 pr-8 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue bg-white appearance-none w-full"
                          required
                        >
                          <option value="">Day</option>
                          {Array.from({ length: 31 }, (_, i) => (
                            <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                              {String(i + 1).padStart(2, '0')}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none text-aa-dark-blue">
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <g clipPath="url(#clip0_6925_33198_day)">
                              <path d="M8 11.5L3.5 6H12.5L8 11.5Z" fill="currentColor"></path>
                            </g>
                            <defs>
                              <clipPath id="clip0_6925_33198_day">
                                <rect width="16" height="16" fill="white" transform="translate(16) rotate(90)"></rect>
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </div>
                      <div className="relative">
                        <select
                          value={formData.dateOfBirth.year}
                          onChange={(e) => handleInputChange('dateOfBirth.year', e.target.value)}
                          className="px-3 py-3 pr-8 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue bg-white appearance-none w-full"
                          required
                        >
                          <option value="">Year</option>
                          {Array.from({ length: 100 }, (_, i) => {
                            const year = new Date().getFullYear() - i;
                            return (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            );
                          })}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none text-aa-dark-blue">
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <g clipPath="url(#clip0_6925_33198_year)">
                              <path d="M8 11.5L3.5 6H12.5L8 11.5Z" fill="currentColor"></path>
                            </g>
                            <defs>
                              <clipPath id="clip0_6925_33198_year">
                                <rect width="16" height="16" fill="white" transform="translate(16) rotate(90)"></rect>
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={formData.gender}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                        className="w-full px-4 py-3 pr-10 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue bg-white appearance-none"
                        required
                      >
                        <option value="">Select gender</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="U">Unspecified</option>
                        <option value="X">Undisclosed</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-aa-dark-blue">
                        <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <g clipPath="url(#clip0_6925_33198_gender)">
                            <path d="M8 11.5L3.5 6H12.5L8 11.5Z" fill="currentColor"></path>
                          </g>
                          <defs>
                            <clipPath id="clip0_6925_33198_gender">
                              <rect width="16" height="16" fill="white" transform="translate(16) rotate(90)"></rect>
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                    </div>
                    <div className="text-[12px] text-gray-500 mt-2">As listed on your photo ID</div>
                  </div>
                </div>

                {/* Residency */}
                <div className="mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                      Country / region of residence <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <select
                          value={formData.residency.country}
                          onChange={(e) => handleInputChange('residency.country', e.target.value)}
                          className="w-full px-4 py-3 pr-10 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue bg-white appearance-none"
                          required
                        >
                          <option value="">Select a country</option>
                          <option value="US">UNITED STATES</option>
                          <option value="CA">CANADA</option>
                          <option value="GB">UNITED KINGDOM</option>
                          <option value="FR">FRANCE</option>
                          <option value="DE">GERMANY</option>
                          <option value="IN">INDIA</option>
                          <option value="CN">CHINA</option>
                          <option value="JP">JAPAN</option>
                          <option value="AU">AUSTRALIA</option>
                          <option value="BR">BRAZIL</option>
                          <option value="MX">MEXICO</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-aa-dark-blue">
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <g clipPath="url(#clip0_6925_33198_country)">
                              <path d="M8 11.5L3.5 6H12.5L8 11.5Z" fill="currentColor"></path>
                            </g>
                            <defs>
                              <clipPath id="clip0_6925_33198_country">
                                <rect width="16" height="16" fill="white" transform="translate(16) rotate(90)"></rect>
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        State
                      </label>
                      <div className="relative">
                        <select
                          value={formData.residency.state}
                          onChange={(e) => handleInputChange('residency.state', e.target.value)}
                          className="w-full px-4 py-3 pr-10 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue bg-white appearance-none"
                          disabled={formData.residency.country !== 'US'}
                        >
                          <option value="">Select state</option>
                          <option value="CA">California</option>
                          <option value="NY">New York</option>
                          <option value="TX">Texas</option>
                          <option value="FL">Florida</option>
                          <option value="IL">Illinois</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-aa-dark-blue">
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <g clipPath="url(#clip0_6925_33198_state)">
                              <path d="M8 11.5L3.5 6H12.5L8 11.5Z" fill="currentColor"></path>
                            </g>
                            <defs>
                              <clipPath id="clip0_6925_33198_state">
                                <rect width="16" height="16" fill="white" transform="translate(16) rotate(90)"></rect>
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Loyalty Program */}
                <div className="mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Loyalty program
                      </label>
                      <div className="relative">
                        <select
                          value={formData.loyalty.program}
                          onChange={(e) => handleInputChange('loyalty.program', e.target.value)}
                          className="w-full px-4 py-3 pr-10 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue bg-white appearance-none"
                        >
                        <option value="">Loyalty program</option>
                        <option value="AA">AAdvantage</option>
                        <option value="AS">Alaska Airlines</option>
                        <option value="BA">British Airways</option>
                        <option value="CX">Cathay Pacific</option>
                        <option value="CZ">China Southern</option>
                        <option value="EY">Etihad Airways</option>
                        <option value="FJ">Fiji Airways</option>
                        <option value="AY">Finnair</option>
                        <option value="IB">Iberia</option>
                        <option value="JL">Japan Airlines</option>
                        <option value="QF">Qantas Airways</option>
                        <option value="QR">Qatar Airways</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-aa-dark-blue">
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <g clipPath="url(#clip0_6925_33198_loyalty)">
                              <path d="M8 11.5L3.5 6H12.5L8 11.5Z" fill="currentColor"></path>
                            </g>
                            <defs>
                              <clipPath id="clip0_6925_33198_loyalty">
                                <rect width="16" height="16" fill="white" transform="translate(16) rotate(90)"></rect>
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Loyalty number
                      </label>
                      <input
                        type="text"
                        value={formData.loyalty.number}
                        onChange={(e) => handleInputChange('loyalty.number', e.target.value)}
                        maxLength="16"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue placeholder-gray-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Secure Traveler Information */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Secure traveler information
                  </h3>
                  
                  {/* Known Traveler Number */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Known traveler number
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={formData.knownTravelerNumber}
                          onChange={(e) => handleInputChange('knownTravelerNumber', e.target.value)}
                          className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue placeholder-gray-500"
                        />
                        <button 
                          type="button" 
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600 hover:text-blue-800"
                          onMouseEnter={() => setShowKnownTravelerTooltip(true)}
                          onMouseLeave={() => setShowKnownTravelerTooltip(false)}
                          aria-label="Known traveler number tooltip"
                        >
                          <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <g clipPath="url(#clip0_7669_34263_ktn)">
                              <path d="M8 1C4.15 1 1 4.15 1 8C1 11.85 4.15 15 8 15C11.85 15 15 11.85 15 8C15 4.15 11.85 1 8 1ZM8 14C4.7 14 2 11.3 2 8C2 4.7 4.7 2 8 2C11.3 2 14 4.7 14 8C14 11.3 11.3 14 8 14ZM8.5 6.5V12H7.5V6.5H8.5ZM8.75 4.75C8.75 5.15 8.4 5.5 8 5.5C7.6 5.5 7.25 5.15 7.25 4.75C7.25 4.35 7.6 4 8 4C8.4 4 8.75 4.35 8.75 4.75Z" fill="currentColor"/>
                            </g>
                            <defs>
                              <clipPath id="clip0_7669_34263_ktn">
                                <rect width="16" height="16" fill="white"/>
                              </clipPath>
                            </defs>
                          </svg>
                          
                          {/* Known Traveler Number Tooltip */}
                          {showKnownTravelerTooltip && (
                            <div 
                              className="absolute top-full right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                              style={{ transform: 'translateX(50%)' }}
                            >
                              <div className="p-4">
                                <div className="font-semibold text-black mb-2 text-left">
                                  Known traveler number tooltip
                                </div>
                                <div className="text-sm text-gray-700 text-left">
                                  If you're in a CBP Trusted Traveler Program (Global Entry, NEXUS, SENTRI), please enter the PASS ID number on the top left corner of your Trusted Traveler Card. Otherwise, please leave this field blank.
                                </div>
                              </div>
                              {/* Arrow */}
                              <div className="absolute bottom-full right-6 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-200"></div>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Issuing country
                      </label>
                      <div className="relative">
                        <select
                          value={formData.knownTravelerCountry}
                          onChange={(e) => handleInputChange('knownTravelerCountry', e.target.value)}
                          className="w-full px-3 py-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white appearance-none"
                        >
                          <option value="">Select a country</option>
                          <option value="US">United States</option>
                          <option value="CA">Canada</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-aa-dark-blue">
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <g clipPath="url(#clip0_6925_33198_known_traveler_country)">
                              <path d="M8 11.5L3.5 6H12.5L8 11.5Z" fill="currentColor"></path>
                            </g>
                            <defs>
                              <clipPath id="clip0_6925_33198_known_traveler_country">
                                <rect width="16" height="16" fill="white" transform="translate(16) rotate(90)"></rect>
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        className="px-6 py-3 text-base text-aa-dark-blue hover:text-aa-dark-blue font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Redress Number */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Redress number
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={formData.redressNumber}
                          onChange={(e) => handleInputChange('redressNumber', e.target.value)}
                          className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue placeholder-gray-500"
                        />
                        <button 
                          type="button" 
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600 hover:text-blue-800"
                          onMouseEnter={() => setShowRedressTooltip(true)}
                          onMouseLeave={() => setShowRedressTooltip(false)}
                          aria-label="Redress number tooltip"
                        >
                          <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <g clipPath="url(#clip0_7669_34263_redress)">
                              <path d="M8 1C4.15 1 1 4.15 1 8C1 11.85 4.15 15 8 15C11.85 15 15 11.85 15 8C15 4.15 11.85 1 8 1ZM8 14C4.7 14 2 11.3 2 8C2 4.7 4.7 2 8 2C11.3 2 14 4.7 14 8C14 11.3 11.3 14 8 14ZM8.5 6.5V12H7.5V6.5H8.5ZM8.75 4.75C8.75 5.15 8.4 5.5 8 5.5C7.6 5.5 7.25 5.15 7.25 4.75C7.25 4.35 7.6 4 8 4C8.4 4 8.75 4.35 8.75 4.75Z" fill="currentColor"/>
                            </g>
                            <defs>
                              <clipPath id="clip0_7669_34263_redress">
                                <rect width="16" height="16" fill="white"/>
                              </clipPath>
                            </defs>
                          </svg>
                          
                          {/* Redress Number Tooltip */}
                          {showRedressTooltip && (
                            <div 
                              className="absolute bottom-full right-0 mb-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                              style={{ transform: 'translateX(50%)' }}
                            >
                              <div className="p-4">
                                <div className="font-semibold text-black mb-2 text-left">
                                  Redress number tooltip
                                </div>
                                <div className="text-sm text-gray-700 text-left">
                                  A redress number is a unique, up to 13-digit number that helps the TSA verify your identity and prevent watch list misidentification. If you filed an inquiry and received a redress number, you can enter it here.
                                </div>
                              </div>
                              {/* Arrow */}
                              <div className="absolute top-full right-6 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-200"></div>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Issuing country
                      </label>
                      <div className="relative">
                        <select
                          value={formData.redressCountry}
                          onChange={(e) => handleInputChange('redressCountry', e.target.value)}
                          className="w-full px-4 py-3 pr-10 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue bg-white appearance-none"
                        >
                          <option value="">Select a country</option>
                          <option value="US">United States</option>
                          <option value="CA">Canada</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-aa-dark-blue">
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <g clipPath="url(#clip0_6925_33198_redress_country)">
                              <path d="M8 11.5L3.5 6H12.5L8 11.5Z" fill="currentColor"></path>
                            </g>
                            <defs>
                              <clipPath id="clip0_6925_33198_redress_country">
                                <rect width="16" height="16" fill="white" transform="translate(16) rotate(90)"></rect>
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        className="px-6 py-3 text-base text-aa-dark-blue hover:text-aa-dark-blue font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Add Secondary Redress Button */}
                  {!showSecondaryRedress && (
                    <div className="mt-4">
                      <div>
                        <span className="t-w-8 t-ml-0">
                          <button 
                            id="secondRedressCheckbox" 
                            type="button"
                            onClick={() => setShowSecondaryRedress(true)}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-aa-dark-blue bg-transparent border border-transparent rounded-md hover:text-aa-dark-blue hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <svg className="w-4 h-4 mr-2 text-aa-dark-blue" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <g clipPath="url(#clip0_6964_33218)">
                                <path d="M8.00005 15C6.15005 15 4.35005 14.25 3.05005 12.95C0.300049 10.2 0.300049 5.8 3.05005 3.05C4.40005 1.75 6.15005 1 8.00005 1C9.85005 1 11.65 1.75 12.95 3.05C15.7 5.8 15.7 10.2 12.95 12.95C11.65 14.25 9.85005 15 8.00005 15ZM8.00005 2C6.40005 2 4.90005 2.6 3.75005 3.75C1.40005 6.1 1.40005 9.9 3.75005 12.25C4.90005 13.4 6.40005 14 8.00005 14C9.60005 14 11.1 13.4 12.25 12.25C14.6 9.9 14.6 6.1 12.25 3.75C11.1 2.6 9.60005 2 8.00005 2ZM12 8.5V7.5H8.50005V4H7.50005V7.5H4.00005V8.5H7.50005V12H8.50005V8.5H12Z" fill="#101419"></path>
                              </g>
                              <defs>
                                <clipPath id="clip0_6964_33218">
                                  <rect width="16" height="16" fill="white"></rect>
                                </clipPath>
                              </defs>
                            </svg>
                            <span className="secure-flight-button text-aa-dark-blue text-[16px]">Add secondary redress number</span>
                          </button>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Secondary Redress Number Section */}
                  {showSecondaryRedress && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Secondary redress number
                        </label>
                        <div className="relative">
                          <input
                            name="docNumber"
                            type="text"
                            value={formData.secondaryRedressNumber}
                            onChange={(e) => handleInputChange('secondaryRedressNumber', e.target.value)}
                            className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue placeholder-gray-500"
                          />
                          <button 
                            type="button" 
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600 hover:text-blue-800"
                            onMouseEnter={() => setShowSecondaryRedressTooltip(true)}
                            onMouseLeave={() => setShowSecondaryRedressTooltip(false)}
                            aria-label="Secondary redress number tooltip"
                          >
                            <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <g clipPath="url(#clip0_7669_34263_secondary)">
                                <path d="M8 1C4.15 1 1 4.15 1 8C1 11.85 4.15 15 8 15C11.85 15 15 11.85 15 8C15 4.15 11.85 1 8 1ZM8 14C4.7 14 2 11.3 2 8C2 4.7 4.7 2 8 2C11.3 2 14 4.7 14 8C14 11.3 11.3 14 8 14ZM8.5 6.5V12H7.5V6.5H8.5ZM8.75 4.75C8.75 5.15 8.4 5.5 8 5.5C7.6 5.5 7.25 5.15 7.25 4.75C7.25 4.35 7.6 4 8 4C8.4 4 8.75 4.35 8.75 4.75Z" fill="currentColor"/>
                              </g>
                              <defs>
                                <clipPath id="clip0_7669_34263_secondary">
                                  <rect width="16" height="16" fill="white"/>
                                </clipPath>
                              </defs>
                            </svg>
                            
                            {/* Secondary Redress Number Tooltip */}
                            {showSecondaryRedressTooltip && (
                              <div 
                                className="absolute bottom-full right-0 mb-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                                style={{ transform: 'translateX(50%)' }}
                              >
                                <div className="p-4">
                                  <div className="font-semibold text-black mb-2 text-left">
                                    Secondary redress number tooltip
                                  </div>
                                  <div className="text-sm text-gray-700 text-left">
                                    A redress number is a unique, up to 13-digit number that helps the TSA verify your identity and prevent watch list misidentification. If you filed an inquiry and received a redress number, you can enter it here.
                                  </div>
                                </div>
                                {/* Arrow */}
                                <div className="absolute top-full right-6 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-200"></div>
                              </div>
                            )}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Issuing country
                        </label>
                        <div className="relative">
                          <select
                            name="country"
                            value={formData.secondaryRedressCountry}
                            onChange={(e) => handleInputChange('secondaryRedressCountry', e.target.value)}
                            className="w-full px-4 py-3 pr-10 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-aa-blue focus:border-aa-blue transition-colors text-aa-blue bg-white appearance-none"
                          >
                            <option value="">Select a country</option>
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-aa-dark-blue">
                            <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <g clipPath="url(#clip0_6925_33198_secondary_country)">
                                <path d="M8 11.5L3.5 6H12.5L8 11.5Z" fill="currentColor"></path>
                              </g>
                              <defs>
                                <clipPath id="clip0_6925_33198_secondary_country">
                                  <rect width="16" height="16" fill="white" transform="translate(16) rotate(90)"></rect>
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-end">
                        <button 
                          type="button"
                          onClick={() => setShowSecondaryRedress(false)}
                          className="px-6 py-3 text-base text-aa-dark-blue hover:text-aa-dark-blue font-medium"
                        >
                          Delete <span className="hidden-accessible">Secondary redress number</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Privacy Policy */}
                <div className="text-[16px] text-gray-600 mb-8">
                  We collect your personal data in accordance with applicable laws and regulations. Read how we use and protect your
                  <br />
                  personal information.
                  <a
                    href="https://www.aa.com/i18n/customer-service/support/privacy-policy.jsp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    American Airlines privacy policy
                    <svg className="w-4 h-4 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>

                {/* Action Buttons */}
                <div className="border-t p-8 flex gap-6 justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-12 py-2 text-lg border-2 border-aa-dark-blue text-blue-600 rounded-lg hover:bg-blue-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-16 py-2 text-lg bg-aa-dark-blue text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PassengerModal;